import { deleteData, getData, patchData, postData } from "../api/api";
import type {
  AnomalyGuide,
  CreateAnomalyGuideRequest,
  UpdateAnomalyGuideRequest,
} from "../types/anomalyGuide";
import { buildReadQueryString } from "../lib/api/readQuery";
import { buildIlikeFilter, quoteRsqlValue } from "../lib/rsql";

const ANOMALY_GUIDE_BASE_URL = "/anomaly-guide";

const normalizeGuideList = (response: unknown): AnomalyGuide[] => {
  if (Array.isArray(response)) {
    return response as AnomalyGuide[];
  }

  if (response && typeof response === "object" && Array.isArray((response as { content?: AnomalyGuide[] }).content)) {
    return (response as { content: AnomalyGuide[] }).content;
  }

  return [];
};

const buildGuideSearchFilter = (search: string): string => {
  const trimmed = search.trim();
  if (!trimmed) {
    return "";
  }

  const quoted = quoteRsqlValue(trimmed);
  return [
    `category=ilike=${quoted}`,
    `subCategory=ilike=${quoted}`,
    `classificationGuidance=ilike=${quoted}`,
  ].join(",");
};

export const anomalyGuideService = {
  getAnomalyGuides: async (
    page: number = 0,
    size: number = 50,
    search?: string,
    fields?: string | string[],
  ) => {
    const searchParam = search ? buildGuideSearchFilter(search) : undefined;

    return getData(
      `${ANOMALY_GUIDE_BASE_URL}/read?${buildReadQueryString({
        page,
        size,
        sort: "createdDate,desc",
        search: searchParam,
        fields,
      })}`,
    );
  },

  getAnomalyGuide: async (id: string) => {
    return getData(`${ANOMALY_GUIDE_BASE_URL}/read/${id}`) as Promise<AnomalyGuide>;
  },

  searchCategories: async (query: string, size: number = 50): Promise<string[]> => {
    const search = buildIlikeFilter("category", query);
    if (!search) {
      return [];
    }

    const response = await getData(
      `${ANOMALY_GUIDE_BASE_URL}/read?${buildReadQueryString({
        page: 0,
        size,
        sort: "createdDate,desc",
        search,
        fields: "category",
      })}`,
    );

    const guides = normalizeGuideList(response);
    const categories = guides
      .map((guide) => guide.category)
      .filter((category): category is string => Boolean(category));

    return Array.from(new Set(categories)).sort((a, b) => a.localeCompare(b));
  },

  listTaxonomy: async (): Promise<
    Array<{ category: string; subCategory: string; classificationGuidance: string }>
  > => {
    const response = await getData(
      `${ANOMALY_GUIDE_BASE_URL}/read?${buildReadQueryString({
        page: 0,
        size: 500,
        sort: "createdDate,desc",
        fields: ["category", "subCategory", "classificationGuidance"],
      })}`,
      {
        skipGlobalLoading: true,
        skipGlobalErrorHandler: true,
      },
    );

    return normalizeGuideList(response)
      .map((guide) => ({
        category: guide.category?.trim() || "",
        subCategory: guide.subCategory?.trim() || "",
        classificationGuidance: guide.classificationGuidance?.trim() || "",
      }))
      .filter((item) => item.category);
  },

  getGuidesByCategory: async (category: string): Promise<AnomalyGuide[]> => {
    const response = await getData(
      `${ANOMALY_GUIDE_BASE_URL}/by-category?category=${encodeURIComponent(category)}`,
    );
    return normalizeGuideList(response);
  },

  getByCategory: async (category: string) => {
    return getData(
      `${ANOMALY_GUIDE_BASE_URL}/by-category?category=${encodeURIComponent(category)}`,
    );
  },

  lookup: async (category: string, subCategory: string) => {
    const params = new URLSearchParams({ category, subCategory });
    return getData(`${ANOMALY_GUIDE_BASE_URL}/lookup?${params.toString()}`);
  },

  createAnomalyGuide: async (data: CreateAnomalyGuideRequest) => {
    return postData(`${ANOMALY_GUIDE_BASE_URL}/create`, data);
  },

  updateAnomalyGuide: async (data: UpdateAnomalyGuideRequest) => {
    return patchData(`${ANOMALY_GUIDE_BASE_URL}/update`, data);
  },

  deleteAnomalyGuide: async (recordId: string) => {
    return deleteData(`${ANOMALY_GUIDE_BASE_URL}/delete/${recordId}`);
  },
};
